import { AlertMessage, AlertType, ButtonType, Form, CustomButton, CustomSubmitButton, Modal, ModalBody, ModalFooter, ModalSize, Select } from 'soprasteria-ui-library';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import * as React from 'react';
import { OrthodonticReferralTreatmentApi } from '../../../../api/OrthodonticReferralTreatmentApi';
import { useAuth } from '../../../../App';
import { setMessage } from '../../../../helpers/MessageHelper';
import { IOrthodonticReferralICD10Code, ISaveOrthodonticReferralICD10Code } from '../../../../models/IOrthodonticReferral';

interface IOrthodonticReferralICD10CodesModal {
    changeSuccessMessage: (message: string) => void;
    getOrthodonticReferral: (id: number, refresh: boolean) => void;
    orthodonticReferralId: number;
    setRenderForm: React.Dispatch<React.SetStateAction<boolean>>;
    renderForm: boolean;
}

export const OrthodonticReferralICD10CodesModal: React.FunctionComponent<IOrthodonticReferralICD10CodesModal> = ({ changeSuccessMessage, getOrthodonticReferral, orthodonticReferralId, renderForm, setRenderForm }) => {

    const auth = useAuth();
    const api = new OrthodonticReferralTreatmentApi(auth);
    const [icd10Codes, setIcd10Codes] = React.useState<IOrthodonticReferralICD10Code[]>();
    const [levelOneICD10Codes, setLevelOneICD10Codes] = React.useState<IListItem[]>();
    const [levelTwoICD10Codes, setLevelTwoICD10Codes] = React.useState<IListItem[]>();
    const [levelThreeICD10Codes, setLevelThreeICD10Codes] = React.useState<IListItem[]>();
    const [isAddingICD10Code, setIsAddingICD10Code] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [selectedICD10CodeId, setSelectedICD10CodeId] = React.useState<number>();

    React.useEffect(() => {
        getICD10Codes();
    }, [])

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }

    const addICD10CodeMapping = (data: ISaveOrthodonticReferralICD10Code) => {
        setIsAddingICD10Code(true);

        data.orthodonticReferralId = orthodonticReferralId;
        data.orthodonticReferralICD10CodeId = selectedICD10CodeId ?? 0;
        api.addICD10CodeMapping(data, () => {
            document.getElementById("close-orthodontic-referral-icd10-codes-modal")?.click();
            setLevelTwoICD10Codes(undefined);
            setLevelThreeICD10Codes(undefined);
            setSelectedICD10CodeId(undefined);
            setRenderForm(false);
            changeSuccessMessage("ICD10-koden har lagts till");
            getOrthodonticReferral(orthodonticReferralId, true);
        }, (error) => changeErrorMessage(error)
            , () => setIsAddingICD10Code(false))
    }

    const mapICD10Codes = (icd10Codes: IOrthodonticReferralICD10Code[]): IListItem[] => {
        return icd10Codes.map<IListItem>((code) => {
            return {
                text: `${(code.code !== "NULL" ? `${code.code} - ` : "")}${code.description}`,
                value: code.orthodonticReferralICD10CodeId.toString()
            }
        });
    }

    const getICD10Codes = () => {
        api.getICD10Codes((newICD10Codes) => {
            setIcd10Codes(newICD10Codes);
            const filteredICD10Codes = newICD10Codes.filter(code => code.level === 1);
            const mappedICD10Codes = mapICD10Codes(filteredICD10Codes);
            setLevelOneICD10Codes(mappedICD10Codes);
        });
    }

    const onChange = (orthodonticReferralICD10CodeId: number) => {
        setSelectedICD10CodeId(orthodonticReferralICD10CodeId);

        if (icd10Codes) {
            const selectedCode = icd10Codes.filter(code => code.orthodonticReferralICD10CodeId === orthodonticReferralICD10CodeId)[0];
            const filteredICD10Codes = icd10Codes.filter(code => code.parentICD10Id === selectedCode?.orthodonticReferralICD10CodeId);

            if (filteredICD10Codes?.length > 0) {
                const mappedICD10Codes = mapICD10Codes(filteredICD10Codes);
                if (selectedCode.level === 1) {
                    setLevelThreeICD10Codes(undefined);
                    setLevelTwoICD10Codes(mappedICD10Codes);
                }
                if (selectedCode.level === 2) {
                    setLevelThreeICD10Codes(mappedICD10Codes);
                }
            }
            else {
                if (selectedCode.level === 1) {
                    setLevelThreeICD10Codes(undefined);
                    setLevelTwoICD10Codes(undefined);
                }
                if (selectedCode.level === 2) {
                    setLevelThreeICD10Codes(undefined);
                }
            }
        }
    }

    return (
        <Modal id="orthodontic-referral-icd10-codes-modal" header="Lägg till ICD10-kod" modalSize={ModalSize.large}>
            <Form onSubmit={addICD10CodeMapping}>
                {
                    renderForm ?
                        <ModalBody>
                            <AlertMessage duration={3000} alertType={AlertType.danger} >{errorMessage}</AlertMessage>
                            <Select label="Kategori 1" name="icd101" options={levelOneICD10Codes ?? []} isLoading={levelOneICD10Codes === undefined}
                                defaultValue={levelOneICD10Codes?.some(code => code.value === selectedICD10CodeId?.toString()) ? selectedICD10CodeId?.toString() : undefined}
                                inlineLabel required placeholder="Gör ett val" onChange={(val) => onChange(parseInt(val))} />
                            {
                                levelTwoICD10Codes ?
                                    <Select label="Kategori 2" name="icd102" options={levelTwoICD10Codes} inlineLabel required placeholder="Gör ett val" onChange={(val) => onChange(parseInt(val))} />
                                    : null
                            }
                            {
                                levelThreeICD10Codes ?
                                    <Select label="Kategori 3" name="icd103" options={levelThreeICD10Codes} inlineLabel required placeholder="Gör ett val" onChange={(val) => onChange(parseInt(val))} />
                                    : null
                            }
                        </ModalBody>
                        : null
                }
                <ModalFooter>
                    <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-orthodontic-referral-icd10-codes-modal" onClick={() => {
                        setLevelTwoICD10Codes(undefined);
                        setLevelThreeICD10Codes(undefined);
                        setSelectedICD10CodeId(undefined);
                        setRenderForm(false);
                    }} />
                    <CustomSubmitButton isLoading={isAddingICD10Code}>Lägg till</CustomSubmitButton>
                </ModalFooter>
            </Form>
        </Modal>
    )
}